/**
 * Secure Token Manager
 *
 * Manages tokens in memory only (not accessible via DevTools state inspection).
 * Provides a secure way to store and retrieve authentication tokens without
 * exposing them in Redux/Zustand state or browser storage.
 *
 * Security Features:
 * - Tokens stored in private class fields (not exposed to DevTools)
 * - No persistence (cleared on page refresh by design)
 * - Token expiry validation
 * - Automatic cleanup of expired tokens
 */

interface TokenData {
  token: string
  expiry: number // timestamp in milliseconds
}

class SecureTokenManager {
  // Private tokens map - not accessible outside this class
  private tokens = new Map<string, TokenData>()
  private cleanupInterval: NodeJS.Timeout | null = null

  constructor() {
    // Start cleanup interval to remove expired tokens
    this.startCleanup()
  }

  /**
   * Set a token with expiry
   * @param key - Token identifier (e.g., 'access', 'org:123')
   * @param token - The JWT token
   * @param expiresInSeconds - Expiry time in seconds (default: 3600 = 1 hour)
   */
  setToken(key: string, token: string, expiresInSeconds: number = 3600): void {
    const expiry = Date.now() + expiresInSeconds * 1000
    this.tokens.set(key, { token, expiry })
  }

  /**
   * Get a token if it exists and is not expired
   * @param key - Token identifier
   * @returns The token or null if not found/expired
   */
  getToken(key: string): string | null {
    const tokenData = this.tokens.get(key)

    if (!tokenData) {
      return null
    }

    // Check if token is expired
    if (Date.now() >= tokenData.expiry) {
      this.tokens.delete(key)
      return null
    }

    return tokenData.token
  }

  /**
   * Check if a token exists and is valid
   * @param key - Token identifier
   * @returns True if token exists and is not expired
   */
  hasValidToken(key: string): boolean {
    return this.getToken(key) !== null
  }

  /**
   * Remove a specific token
   * @param key - Token identifier
   */
  removeToken(key: string): void {
    this.tokens.delete(key)
  }

  /**
   * Clear all tokens
   */
  clearAllTokens(): void {
    this.tokens.clear()
  }

  /**
   * Clear all organization tokens (tokens starting with 'org:')
   */
  clearOrganizationTokens(): void {
    const keysToDelete: Array<string> = []

    this.tokens.forEach((_, key) => {
      if (key.startsWith('org:')) {
        keysToDelete.push(key)
      }
    })

    keysToDelete.forEach((key) => this.tokens.delete(key))
  }

  /**
   * Get all token keys (for debugging purposes only)
   * Does not return actual tokens
   */
  getTokenKeys(): Array<string> {
    return Array.from(this.tokens.keys())
  }

  /**
   * Check if a token is expired without removing it
   * @param key - Token identifier
   * @returns True if token exists and is expired
   */
  isTokenExpired(key: string): boolean {
    const tokenData = this.tokens.get(key)

    if (!tokenData) {
      return true // Treat missing token as expired
    }

    return Date.now() >= tokenData.expiry
  }

  /**
   * Get time until token expires
   * @param key - Token identifier
   * @returns Seconds until expiry, or 0 if expired/missing
   */
  getTimeUntilExpiry(key: string): number {
    const tokenData = this.tokens.get(key)

    if (!tokenData) {
      return 0
    }

    const timeRemaining = Math.max(0, tokenData.expiry - Date.now())
    return Math.floor(timeRemaining / 1000) // Convert to seconds
  }

  /**
   * Start automatic cleanup of expired tokens
   */
  private startCleanup(): void {
    // Clean up expired tokens every 5 minutes
    this.cleanupInterval = setInterval(
      () => {
        this.cleanupExpiredTokens()
      },
      5 * 60 * 1000,
    )
  }

  /**
   * Stop automatic cleanup
   */
  stopCleanup(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval)
      this.cleanupInterval = null
    }
  }

  /**
   * Remove all expired tokens
   */
  private cleanupExpiredTokens(): void {
    const now = Date.now()
    const keysToDelete: Array<string> = []

    this.tokens.forEach((tokenData, key) => {
      if (now >= tokenData.expiry) {
        keysToDelete.push(key)
      }
    })

    keysToDelete.forEach((key) => this.tokens.delete(key))

    void keysToDelete.length // cleanup complete
  }

  /**
   * Decode JWT payload (without verification)
   * Useful for debugging and getting token metadata
   * @param token - The JWT token
   * @returns Decoded payload or null if invalid
   */
  decodeToken(token: string): Record<string, any> | null {
    try {
      const parts = token.split('.')
      if (parts.length !== 3) {
        return null
      }

      const payload = parts[1]
      const decoded = atob(payload)
      return JSON.parse(decoded)
    } catch {
      return null
    }
  }
}

// Export singleton instance
export const secureTokenManager = new SecureTokenManager()

// Export class for testing purposes
export { SecureTokenManager }
